import React, { useRef, useState, useEffect } from "react";
import { Printer, Pencil, X, Save } from "lucide-react";
import { useLanguage } from "../StoreContext/LanguageContext";
import apiService from "../config";
import { toast } from "react-toastify";

/* ===================== MARATHI NUMBER CONVERTER ===================== */
const marathiNumbers = [
  "शून्य", "एक", "दोन", "तीन", "चार", "पाच", "सहा", "सात", "आठ", "नऊ", "दहा",
  "अकरा", "बारा", "तेरा", "चौदा", "पंधरा", "सोळा", "सतरा", "अठरा", "एकोणीस", "वीस",
  "एकवीस", "बावीस", "तेवीस", "चोवीस", "पंचवीस", "सव्वीस", "सत्तावीस", "अठ्ठावीस", "एकोणतीस", "तीस",
  "एकतीस", "बत्तीस", "तेहतीस", "चौतीस", "पस्तीस", "छत्तीस", "सदतीस", "अडतीस", "एकोणचाळीस", "चाळीस",
  "एक्केचाळीस", "बेचाळीस", "त्रेचाळीस", "चव्वेचाळीस", "पंचेचाळीस", "शेहेचाळीस", "सत्तेचाळीस", "अठ्ठेचाळीस", "एकोणपन्नास", "पन्नास",
  "एकावन्न", "बावन्न", "त्रेपन्न", "चोपन्न", "पंचावन्न", "छप्पन्न", "सत्तावन्न", "अठ्ठावन्न", "एकोणसाठ", "साठ",
  "एकसष्ठ", "बासष्ठ", "त्रेसष्ठ", "चौसष्ठ", "पासष्ठ", "सहासष्ठ", "सदुसष्ठ", "अडुसष्ठ", "एकोणसत्तर", "सत्तर",
  "एकाहत्तर", "बाहत्तर", "त्र्याहत्तर", "चौऱ्याहत्तर", "पंच्याहत्तर", "शहात्तर", "सत्याहत्तर", "अठ्ठ्याहत्तर", "एकोणऐंशी", "ऐंशी",
  "एक्क्याऐंशी", "ब्याऐंशी", "त्र्याऐंशी", "चौऱ्याऐंशी", "पंच्याऐंशी", "शहाऐंशी", "सत्याऐंशी", "अठ्ठ्याऐंशी", "एकोणनव्वद", "नव्वद",
  "एक्क्याण्णव", "ब्याण्णव", "त्र्याण्णव", "चौऱ्याण्णव", "पंच्याण्णव", "शहाण्णव", "सत्याण्णव", "अठ्ठ्याण्णव", "नव्व्याण्णव"
];

const numberToMarathiWords = (num) => {
  if (num === null || num === undefined || isNaN(num)) return "";
  let n = Math.floor(Number(num));
  if (n === 0) return "शून्य";

  let words = [];
  let crores = Math.floor(n / 10000000);
  n -= crores * 10000000;
  let lakhs = Math.floor(n / 100000);
  n -= lakhs * 100000;
  let thousands = Math.floor(n / 1000);
  n -= thousands * 1000;
  let hundreds = Math.floor(n / 100);
  n -= hundreds * 100;

  if (crores > 0) {
    words.push(crores < 100 ? marathiNumbers[crores] : numberToMarathiWords(crores));
    words.push("कोटी");
  }
  if (lakhs > 0) {
    words.push(marathiNumbers[lakhs] + " लाख");
  }
  if (thousands > 0) {
    words.push(marathiNumbers[thousands] + " हजार");
  }
  if (hundreds > 0) {
    words.push(hundreds === 1 ? "शंभर" : marathiNumbers[hundreds] + "शे");
  }
  if (n > 0) {
    words.push(marathiNumbers[n]);
  }

  return words.join(" ");
};


const KabjaPauti = ({ onBack }) => {
  const documentRef = useRef(null);
  const { language, translations } = useLanguage();

  /* ===================== STATE ===================== */
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedOwnerIndex, setSelectedOwnerIndex] = useState(null);

  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [currentProposalId, setCurrentProposalId] = useState(null);
  const [currentLaoId, setCurrentLaoId] = useState(null);

  const [compensationData, setCompensationData] = useState([]);
  const [proposalNo, setProposalNo] = useState('');
  const [laoName, setLaoName] = useState('');

  const [formData, setFormData] = useState({
    tabapavti_date: "",
    tabapavti_default_content: "", 
    tabapavti_witness1: "",
    tabapavti_witness2: "",
    tabapavti_transferred_to: "",
    tabapavti_trsanferred_date: "",
    tabapavti_representative_name: "",
    tabapavti_representative_designation: ""
  });

  const formatDate = (dateString) => {
    if (!dateString || dateString === '1900-01-01T00:00:00.000Z') return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatCurrency = (amount) => {
    const numericAmount = parseFloat(amount) || 0;
    return numericAmount; 
  };

  // Helper function to generate the text content for the textarea
  const generateDefaultParagraph = (owner) => {
    const pNo = proposalNo || "-------------";
    const tDate = owner.tabapavti_date ? formatDate(owner.tabapavti_date) : "------------";
    const amt = formatCurrency(owner.total_compensation) || "-------------------";
    const amtWords = numberToMarathiWords(owner.total_compensation) || "--------------------------";
    
    return `मी / आम्ही, खालील सही करणार पावती लिहून देतो की, भूसंपादन प्रकरण क्रमांक ${pNo} मा.जिल्हाधिकारी बुलडाणा यामध्ये दिनांक ${tDate} रोजीच्या अंतिम निवाडयानुसार संपादीत होणाऱ्या खालीलप्रमाणे आमच्या जमिनीचा त्यामधील संलग्न सर्व बाबीच्या मुल्यांकनावरील मोबदला रक्कम रु. ${amt}/- (अक्षरी ${amtWords} फक्त) मला / आम्हाला मिळाले. त्याबाबत माझी/आमची काहीही तक्रार राहणार नाही. सदरची रक्कम मी / आम्ही निषेधासह / निषेधाशिवाय स्विकारीत आहे.`;
  };

  /* ===================== GET PROJECT / PROPOSAL ===================== */
  useEffect(() => {
    const getProjectData = () => {
      const selectedProjectData = localStorage.getItem('selectedProject');
      const currentProposal = localStorage.getItem('userData');

      if (selectedProjectData) {
        const selectedProject = JSON.parse(selectedProjectData);
        setCurrentProjectId(selectedProject.id || selectedProject.project_id || selectedProject.PROJECT_ID);
      }

      if (currentProposal) {
        const selectedProposal = JSON.parse(currentProposal);
        setCurrentProposalId(selectedProposal.default_proposal || selectedProposal.proposal_id);
        setCurrentLaoId(selectedProposal.usr_id);
      }
    };
    getProjectData();
  }, []);

  useEffect(() => {
    const fetchAdditionalDetails = async () => {
      if (!currentProjectId || !currentProposalId) return;
      try {
        const result = await apiService.getProposalPublish(currentProjectId, currentProposalId);
        if (result.status === 200 && result.data) {
          setProposalNo(result.data.section_11_bhusampadan_no || result.data.acquisition_proposal_no || '-------------');
        }

        const role = 3; // LAO Role
        const laoRes = await apiService.getAssignLAO(role, currentProjectId, currentProposalId);
        if (laoRes.status === 200 && laoRes.data && laoRes.data.length > 0) {
          setLaoName(laoRes.data[0].full_name || 'भूसंपादन अधिकारी');
        }
      } catch (error) {
        console.error('Error fetching details:', error);
      }
    };
    fetchAdditionalDetails();
  }, [currentProjectId, currentProposalId]);

  /* ===================== GET DATA ===================== */
  useEffect(() => {
    const fetchTabapavti = async () => {
      if (!currentProjectId || !currentProposalId) return;

      try {
        setLoading(true);
        const response = await apiService.getKJPTabapavti(currentProjectId, currentProposalId);

        const rawData = response?.data || response || [];
        const dataArray = Array.isArray(rawData) ? rawData : [];

        if (dataArray.length > 0) {
          setCompensationData(dataArray);
        }
      } catch (err) {
        console.warn("Tabapavti data not found or error fetching", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTabapavti();
  }, [currentProjectId, currentProposalId]);

  /* ===================== HANDLERS ===================== */
  const handleEditClick = (ownerIndex) => {
    const owner = compensationData[ownerIndex];
    setSelectedOwnerIndex(ownerIndex);

    setFormData({
      owner_name: owner.owner_name || "",
      tabapavti_date: owner.tabapavti_date ? owner.tabapavti_date.split('T')[0] : "",
      tabapavti_default_content: owner.tabapavti_default_content || generateDefaultParagraph(owner),
      tabapavti_witness1: owner.tabapavti_witness1 || "",
      tabapavti_witness2: owner.tabapavti_witness2 || "",
      tabapavti_transferred_to: owner.tabapavti_transferred_to || "",
      tabapavti_trsanferred_date: owner.tabapavti_trsanferred_date ? owner.tabapavti_trsanferred_date.split('T')[0] : "",
      tabapavti_representative_name: owner.tabapavti_representative_name || "",
      tabapavti_representative_designation: owner.tabapavti_representative_designation || ""
    });

    setOpen(true);
  };

  const handleSave = async () => {
    if (!currentProjectId || !currentProposalId) {
      toast.error("Project / Proposal ID missing");
      return;
    }

    try {
      setSaving(true);
      const owner = compensationData[selectedOwnerIndex];

      const payload = {
        project_id: currentProjectId,
        proposal_id: currentProposalId,
        owner_id: owner.land_owner_id || owner.owner_id,
        tabapavti_date: formData.tabapavti_date,
        tabapavti_witness1: formData.tabapavti_witness1,
        tabapavti_witness2: formData.tabapavti_witness2,
        tabapavti_transferred_to: formData.tabapavti_transferred_to,
        tabapavti_trsanferred_date: formData.tabapavti_trsanferred_date,
        tabapavti_representative_name: formData.tabapavti_representative_name,
        tabapavti_representative_designation: formData.tabapavti_representative_designation,
        tabapavti_default_content: formData.tabapavti_default_content, 
      };

      const response = await apiService.postAddUpdateKJPTabapavti(payload);

      if (response.status === 200 || response.status === 201) {
        const updatedList = compensationData.map((item, idx) => {
          if (idx !== selectedOwnerIndex) return item;
          return {
            ...item,
            tabapavti_date: formData.tabapavti_date,
            tabapavti_witness1: formData.tabapavti_witness1,
            tabapavti_witness2: formData.tabapavti_witness2,
            tabapavti_transferred_to: formData.tabapavti_transferred_to,
            tabapavti_trsanferred_date: formData.tabapavti_trsanferred_date,
            tabapavti_representative_name: formData.tabapavti_representative_name,
            tabapavti_representative_designation: formData.tabapavti_representative_designation,
            tabapavti_default_content: formData.tabapavti_default_content
          };
        });

        setCompensationData(updatedList);
        setOpen(false);
        toast.success("ताबा पावती यशस्वीपणे जतन झाली");
      }
    } catch (err) {
      console.error("Save failed:", err);
      toast.error("जतन करताना त्रुटी आली");
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    const printContent = documentRef.current?.innerHTML;

    if (!printContent) {
      toast.error("प्रिंट करण्यासाठी कंटेंट उपलब्ध नाही.");
      return;
    }

    const printWindow = window.open("", "", "height=900,width=1000");

    printWindow.document.write(`
      <html>
        <head>
          <title>ताबा पावती</title>
          <meta charset="UTF-8">
          <style>
            @page {
              size: A4 portrait;
              margin: 1cm; /* Reduced margin to fit fully */
            }
            body {
              font-family: "Noto Serif Devanagari", "Mangal", serif;
              font-size: 15px; /* Scaled slightly for perfect 1-page fit */
              line-height: 1.4;
              color: #000;
              margin: 0;
              padding: 0;
            }
            
            /* Page Break Logic */
            .page {
              page-break-after: always !important;
              break-after: page !important;
              page-break-inside: avoid !important;
              width: 100% !important;
            }
            .page:last-of-type {
              page-break-after: auto !important;
              break-after: auto !important;
            }

            .text-center { text-align: center !important; }
            .w-full { width: 100% !important; }
            .block { display: block !important; }
            .underline { text-decoration: underline !important; }
            .font-bold { font-weight: bold !important; }
            .font-semibold { font-weight: 600 !important; }
            .para { text-align: justify; margin: 12px 0; text-indent: 40px; }
            
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 15px 0;
              border: 1px solid #000;
            }
            table th, table td {
              border: 1px solid #000 !important;
              padding: 8px;
              vertical-align: middle;
              text-align: center;
            }
            th { font-weight: bold; }
            
            .flex { display: flex; }
            .justify-between { justify-content: space-between; }
            .justify-end { justify-content: flex-end; }
            .items-start { align-items: flex-start; }
            .items-end { align-items: flex-end; }
            
            /* Compress Spacing for Print */
            .mt-4 { margin-top: 10px !important; }
            .mt-6 { margin-top: 15px !important; }
            .mt-8 { margin-top: 20px !important; }
            .mb-2 { margin-bottom: 5px !important; }
            .mb-4 { margin-bottom: 10px !important; }
            .mb-6 { margin-bottom: 15px !important; }
            .mb-8 { margin-bottom: 20px !important; }
            .my-6 { margin-top: 15px !important; margin-bottom: 15px !important; }
            .my-8 { margin-top: 20px !important; margin-bottom: 20px !important; }
            
            .text-lg { font-size: 16px; }
            .text-xl { font-size: 18px; }
            .text-2xl { font-size: 20px; }
            
            .no-print, button, .noprint {
              display: none !important;
            }
            @media print {
              body {
                print-color-adjust: exact;
                -webkit-print-color-adjust: exact;
              }
            }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
      </html>
    `);

    printWindow.document.close();

    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 800);
  };

  const validOwners = compensationData.map((owner, index) => ({ ...owner, originalIndex: index }));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  /* ===================== UI ===================== */
  return (
    <div className="bg-gray-50 min-h-screen pb-10">
      <style>{`
        @media print {
          body { background: white; }
          .no-print { display: none !important; }
          .page {
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            border: none !important;
            box-shadow: none !important;
            page-break-after: always !important;
            break-after: page !important;
            page-break-inside: avoid !important;
          }
          .page:last-of-type {
            page-break-after: auto !important;
            break-after: auto !important;
          }
        }
        .page {
          width: 820px;
          margin: 20px auto;
          padding: 40px;
          font-family: "Noto Serif Devanagari", serif;
          font-size: 16px;
          background: #fff;
          color: #000;
        }
      `}</style>

      {/* ACTION BUTTONS */}
      <div className="no-print flex justify-end gap-3 p-4 bg-white shadow-sm border-b ">
        <button
          onClick={handlePrint}
          className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white font-medium rounded-lg shadow hover:bg-green-700 transition-colors"
        >
          <Printer size={18} /> Print All
        </button>
      </div>

      <div ref={documentRef} className="mt-6">
        {validOwners && validOwners.length > 0 ? (
          validOwners.map((owner, index) => (
            <div
              key={owner.tabapavti_id || owner.originalIndex}
              className="page shadow-2xl rounded-sm border border-gray-200"
              style={{ position: "relative" }}
            >
       <div className="flex justify-between items-start mb-4">
  
  {/* LEFT: Per-owner Edit button */}
  <div className="no-print">
    <button
      onClick={() => handleEditClick(owner.originalIndex)}
      className="flex items-center gap-2 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm transition-colors shadow-sm"
    >
      <Pencil size={14} /> संपादित करा (Edit)
    </button>
  </div>

  {/* RIGHT: SRNO (Inline styles guarantee it works in the print window without Tailwind) */}
  <div style={{ marginLeft: "auto", textAlign: "right" }}>
    
    {/* Print version of SRNO */}
    <div className="font-bold text-sm hidden print:block">
      SRNO: {index + 1}
    </div>
    
    {/* Screen version of SRNO */}
    <div className="font-bold text-sm px-3 py-1 no-print">
      SRNO: {index + 1}
    </div>
    
  </div>

</div>

              {/* DOCUMENT CONTENT */}
              <div 
                className="w-full font-bold text-2xl mb-6 underline" 
                style={{ textAlign: 'center' }}
              >
                ताबा पावती
              </div>
              
              <div className="mb-2 font-semibold">लिहून घेणार :- महाराष्ट्र शासन तर्फे (भूसंपादन अधिकारी)</div>
              <div className="mb-6 font-semibold">पावती लिहून देणार :- शेतकरी</div>
              
              <div 
                className="w-full text-xl mb-4" 
                style={{ textAlign: 'center' }}
              >
                *****
              </div>

              {owner.tabapavti_default_content ? (
                 <p className="para mb-6 whitespace-pre-wrap">{owner.tabapavti_default_content}</p>
              ) : (
                <p className="para mb-6">
                  मी / आम्ही, खालील सही करणार पावती लिहून देतो की, भूसंपादन प्रकरण क्रमांक <b>{proposalNo || "-------------"}</b> मा.जिल्हाधिकारी बुलडाणा यामध्ये दिनांक <b>{owner.tabapavti_date ? formatDate(owner.tabapavti_date) : "------------"}</b> रोजीच्या अंतिम निवाडयानुसार संपादीत होणाऱ्या खालीलप्रमाणे आमच्या जमिनीचा त्यामधील संलग्न सर्व बाबीच्या मुल्यांकनावरील मोबदला रक्कम रु. <b>{formatCurrency(owner.total_compensation) || "-------------------"}/-</b> (अक्षरी <b>{numberToMarathiWords(owner.total_compensation) || "--------------------------"}</b> फक्त) मला / आम्हाला मिळाले. त्याबाबत माझी/आमची काहीही तक्रार राहणार नाही. सदरची रक्कम मी / आम्ही निषेधासह / निषेधाशिवाय स्विकारीत आहे.
                </p>
              )}

              <table className="w-full border-collapse border border-black my-6">
                <thead>
                  <tr className="bg-gray-50 border border-black">
                    <th className="border border-black p-2 text-center font-bold">गावाचे नाव</th>
                    <th className="border border-black p-2 text-center font-bold">गट क्रमांक</th>
                    <th className="border border-black p-2 text-center font-bold">लागवड योग्य क्षेत्र (हे.आ.)</th>
                    <th className="border border-black p-2 text-center font-bold">पो.ख. क्षेत्र (हे.आ.)</th>
                    <th className="border border-black p-2 text-center font-bold">एकूण क्षेत्र (हे.आ.)</th>
                    <th className="border border-black p-2 text-center font-bold">एकुण भरपाई रक्क्म (रुपये)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border border-black">
                    <td className="border border-black p-2 text-center">{owner.village_name || "-"}</td>
                    <td className="border border-black p-2 text-center">{owner.survey_no || "-"}</td>
                    <td className="border border-black p-2 text-center">{owner.owner_aqr_area || "0"}</td>
                    <td className="border border-black p-2 text-center">{(parseFloat(owner.owner_total_area || 0) - parseFloat(owner.owner_aqr_area || 0)).toFixed(2)}</td>
                    <td className="border border-black p-2 text-center">{owner.owner_total_area || "-"}</td>
                    <td className="border border-black p-2 text-center">{owner.total_compensation || "-"}</td>
                  </tr>
                </tbody>
              </table>

              <p className="mb-6">
                (अक्षरी- <b>{numberToMarathiWords(owner.total_compensation) || "-----------------------------"}</b> रु. फक्त)
              </p>

              <div className="flex justify-end mb-6">
                <div style={{ textAlign: 'center' }}>
                  <div className="mb-8 font-semibold">भूधारकाची सही-</div>
                  <div className="font-bold text-lg">{owner.owner_name}</div>
                  <div className="mt-2">दिनांक : {owner.tabapavti_date ? formatDate(owner.tabapavti_date) : ""}</div>
                </div>
              </div>

              <div className="flex justify-between mt-4">
                <div>
                  <div className="font-bold mb-4">साक्षीदार:-</div>
                  <div className="mb-4">१) {owner.tabapavti_witness1 || "_____________________________"}</div>
                  <div className="mb-2">२) {owner.tabapavti_witness2 || "_____________________________"}</div>
                </div>
                <div className="mt-4" style={{ textAlign: 'center' }}>
                  <div className="font-bold mb-8">ताबा घेणाऱ्याची सही</div>
                  <div className="font-bold">भूसंपादन अधिकारी</div>
                </div>
              </div>

              <div 
                className="w-full my-6 block" 
                style={{ textAlign: 'center', letterSpacing: '1px', overflow: 'hidden' }}
              >
                --------------------------------------------------------------------------------------------------------------------------
              </div>

              <p className="mb-6">
                वरीलप्रमाणे संपादीत जमिनीचा / जागेचा त्यामधील संलग्न सर्व बाबीसह ताबा <b>{owner.tabapavti_transferred_to || "-----------------------------------"}</b> यांना हस्तांतरीत केला आहे.
              </p>

              <div className="flex justify-between items-end mt-6">
                <div>
                  <div className="mb-4">दिनांक :- {owner.tabapavti_trsanferred_date ? formatDate(owner.tabapavti_trsanferred_date) : "_____________"}</div>
                  <div className="font-bold mb-4">संपादन संस्थेचे प्रतिनिधी-</div>
                  <div className="mb-2">स्वाक्षरी :- ________________</div>
                  <div className="mb-2">नाव :- {owner.tabapavti_representative_name || "________________"}</div>
                  <div>पद  :- {owner.tabapavti_representative_designation || "________________"}</div>
                </div>
                <div className="font-bold" style={{ textAlign: 'center' }}>
                  भूसंपादन अधिकारी
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center text-gray-500 py-20 bg-white rounded-lg shadow mx-8">
            <div className="text-xl">कोणताही डेटा उपलब्ध नाही (No records found)</div>
          </div>
        )}
      </div>

      {/* ===================== EDIT MODAL ===================== */}
      {open && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-200">
            <div className="flex justify-between items-center mb-6 border-b pb-4">
              <h2 className="text-xl font-bold text-gray-800">
                {selectedOwnerIndex !== null
                  ? `पावती संपादित करा - ${compensationData[selectedOwnerIndex]?.owner_name}`
                  : "पावती संपादित करा"}
              </h2>
              <button onClick={() => setOpen(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                <X size={22} className="text-gray-600" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              <div className="col-span-1 md:col-span-2">
                <label className="block font-semibold text-gray-700 mb-1">मुख्य मजकूर (Main Paragraph)</label>
                <textarea
                  rows="5"
                  value={formData.tabapavti_default_content}
                  onChange={(e) => setFormData({ ...formData, tabapavti_default_content: e.target.value })}
                  className="w-full border border-gray-300 p-2.5 rounded focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block font-semibold text-gray-700 mb-1">दिनांक (Date) *</label>
                <input
                  type="date"
                  value={formData.tabapavti_date}
                  onChange={(e) => setFormData({ ...formData, tabapavti_date: e.target.value })}
                  className="w-full border border-gray-300 p-2.5 rounded focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="md:col-start-1">
                <label className="block font-semibold text-gray-700 mb-1">साक्षीदार १ (Witness 1)</label>
                <input
                  value={formData.tabapavti_witness1}
                  onChange={(e) => setFormData({ ...formData, tabapavti_witness1: e.target.value })}
                  placeholder="साक्षीदाराचे नाव"
                  className="w-full border border-gray-300 p-2.5 rounded focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block font-semibold text-gray-700 mb-1">साक्षीदार २ (Witness 2)</label>
                <input
                  value={formData.tabapavti_witness2}
                  onChange={(e) => setFormData({ ...formData, tabapavti_witness2: e.target.value })}
                  placeholder="साक्षीदाराचे नाव"
                  className="w-full border border-gray-300 p-2.5 rounded focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="col-span-1 md:col-span-2 mt-4 border-t pt-4">
                <h3 className="font-bold text-gray-700 mb-4">संपादन संस्थेचे प्रतिनिधी (Representative Details)</h3>
              </div>

              <div>
                <label className="block font-semibold text-gray-700 mb-1">हस्तांतरीत केले (Transferred To)</label>
                <input
                  value={formData.tabapavti_transferred_to}
                  onChange={(e) => setFormData({ ...formData, tabapavti_transferred_to: e.target.value })}
                  placeholder="नाव"
                  className="w-full border border-gray-300 p-2.5 rounded focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block font-semibold text-gray-700 mb-1">हस्तांतरण दिनांक (Transfer Date)</label>
                <input
                  type="date"
                  value={formData.tabapavti_trsanferred_date}
                  onChange={(e) => setFormData({ ...formData, tabapavti_trsanferred_date: e.target.value })}
                  className="w-full border border-gray-300 p-2.5 rounded focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block font-semibold text-gray-700 mb-1">प्रतिनिधीचे नाव (Rep Name)</label>
                <input
                  value={formData.tabapavti_representative_name}
                  onChange={(e) => setFormData({ ...formData, tabapavti_representative_name: e.target.value })}
                  placeholder="प्रतिनिधीचे नाव"
                  className="w-full border border-gray-300 p-2.5 rounded focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block font-semibold text-gray-700 mb-1">प्रतिनिधीचे पद (Rep Designation)</label>
                <input
                  value={formData.tabapavti_representative_designation}
                  onChange={(e) => setFormData({ ...formData, tabapavti_representative_designation: e.target.value })}
                  placeholder="पद"
                  className="w-full border border-gray-300 p-2.5 rounded focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-8 border-t pt-4">
              <button
                onClick={() => setOpen(false)}
                className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>

              <button
                onClick={handleSave}
                disabled={saving || !formData.tabapavti_date}
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60 transition-colors"
              >
                {saving ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={18} /> Save Details
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KabjaPauti;